import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "./AuthShell";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MailCheck, Check, X, ArrowLeft } from "lucide-react";

const RULES = [
  { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "At least one number", test: (p) => /\d/.test(p) },
];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "reset" | "done"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);

  const passed = RULES.every((r) => r.test(password));
  const match = password.length > 0 && password === confirm;

  const sendCode = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists, a 6-digit code is on its way.");
      setStep("reset");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("A new code has been sent.");
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { toast.error("Enter the 6-digit code from your email."); return; }
    if (!passed) { toast.error("Password does not meet the requirements."); return; }
    if (!match) { toast.error("Passwords do not match."); return; }
    setResetting(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), password });
      setStep("done");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResetting(false);
    }
  };

  if (step === "done") {
    return (
      <AuthShell title="Password reset" subtitle="You're all set.">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="reset-success">
          <MailCheck className="w-10 h-10 text-emerald-500 mx-auto" />
          <p className="mt-3 text-slate-700">Your password has been reset successfully. You can now log in with your new password.</p>
          <Button className="mt-5 rounded-xl w-full" data-testid="reset-goto-login" onClick={() => navigate("/login")}>Go to Login</Button>
        </div>
      </AuthShell>
    );
  }

  if (step === "reset") {
    return (
      <AuthShell
        title="Enter your reset code"
        subtitle={<>We sent a 6-digit code to <strong>{email}</strong>. Enter it below with your new password.</>}
        footer={<Link to="/login" className="text-primary font-semibold" data-testid="link-back-login">Back to login</Link>}
      >
        <form onSubmit={doReset} className="space-y-4" data-testid="reset-form">
          <div className="space-y-1.5">
            <Label>6-digit code</Label>
            <Input data-testid="reset-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456" inputMode="numeric" maxLength={6} className="rounded-xl h-11 font-mono tracking-[0.5em] text-center text-lg" required />
            <button type="button" onClick={resend} disabled={sending} className="text-xs text-primary font-medium disabled:opacity-50" data-testid="reset-resend-btn">
              {sending ? "Sending…" : "Didn't get it? Resend code"}
            </button>
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <PasswordInput data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-11" required />
          </div>
          <ul className="space-y-1 rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="password-requirements">
            {RULES.map((r) => {
              const ok = r.test(password);
              return (
                <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-slate-400"}`}>
                  {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {r.label}
                </li>
              );
            })}
          </ul>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <PasswordInput data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-xl h-11" required />
            {confirm.length > 0 && !match && <p className="text-xs text-rose-500">Passwords do not match.</p>}
          </div>
          <Button type="submit" disabled={resetting} data-testid="reset-submit-button" className="w-full rounded-xl h-11">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
          </Button>
          <button type="button" onClick={() => setStep("email")} className="w-full text-xs text-slate-500 flex items-center justify-center gap-1" data-testid="reset-change-email">
            <ArrowLeft className="w-3 h-3" /> Use a different email
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send you a 6-digit code."
      footer={<Link to="/login" className="text-primary font-semibold" data-testid="link-back-login">Back to login</Link>}
    >
      <form onSubmit={sendCode} className="space-y-4" data-testid="forgot-form">
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl h-11" required />
        </div>
        <Button type="submit" disabled={sending} data-testid="forgot-submit-button" className="w-full rounded-xl h-11">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"}
        </Button>
      </form>
    </AuthShell>
  );
}
